import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Badge,
  Modal,
  Text,
  Paper,
  Group,
  Loader,
  Center,
  ScrollArea,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Box,
  Grid,
  Avatar,
  Divider,
  Select, // Import Select
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconEye,
  IconInfoCircle,
  IconRefresh,
  IconSchool,
  IconChefHat,
  IconCalendar,
  IconReportMedical,
  IconStethoscope,
  IconChartPie,
  IconFilter, // Import Icon Filter
} from "@tabler/icons-react";
import api from "../../services/api";

export default function AdminLaporanNutrisiPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // State Filter
  const [filterSekolah, setFilterSekolah] = useState(null);

  // Modal State
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/laporan-nutrisi");
      const hasil = response.data.data || response.data;
      setData(Array.isArray(hasil) ? hasil : []);
    } catch (error) {
      console.error("Gagal Load Data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. LOGIKA FILTERING ---

  // A. Ambil daftar sekolah unik untuk opsi Dropdown
  const schoolOptions = Array.from(
    new Set(
      data
        .map((item) => item.sekolah?.nama_sekolah) // Ambil nama sekolah dari relasi
        .filter((nama) => nama) // Hapus yang null/undefined
    )
  ).map((nama) => ({ value: nama, label: nama }));

  // B. Filter Data List berdasarkan pilihan dropdown
  const filteredData = data.filter((item) => {
    if (!filterSekolah) return true; // Tampilkan semua jika filter kosong
    return item.sekolah?.nama_sekolah === filterSekolah;
  });

  // --- 3. HANDLERS ---
  const handleViewDetail = (item) => {
    setSelectedItem(item);
    open();
  };

  // --- 4. RENDER UI ---
  if (loading)
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );

  // --- GENERATE ROWS (Gunakan filteredData, BUKAN data) ---
  const rows = filteredData.map((item) => {
    const hasReport = !!item.laporan_nutrisi;
    const report = item.laporan_nutrisi || {};

    return (
      <Table.Tr key={item.id}>
        {/* Kolom Tanggal */}
        <Table.Td>
          <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString("id-ID", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Text>
          <Text fz="xs" c="dimmed">
            {new Date(item.tanggal_distribusi).getFullYear()}
          </Text>
        </Table.Td>

        {/* Kolom Sekolah */}
        <Table.Td>
          <Group gap="sm">
            <ThemeIcon variant="light" color="blue" radius="xl" size="lg">
              <IconSchool size={18} />
            </ThemeIcon>
            <div>
              <Text fz="sm" fw={500}>
                {item.sekolah?.nama_sekolah}
              </Text>
              <Text fz="xs" c="dimmed">
                Tujuan
              </Text>
            </div>
          </Group>
        </Table.Td>

        {/* Kolom Menu */}
        <Table.Td>
          <Group gap="sm">
            <ThemeIcon variant="light" color="orange" radius="xl" size="lg">
              <IconChefHat size={18} />
            </ThemeIcon>
            <Text fz="sm" fw={500}>
              {item.menu?.nama_menu}
            </Text>
          </Group>
        </Table.Td>

        {/* Kolom Status & Supervisor */}
        <Table.Td>
          {hasReport ? (
            <div>
              <Badge color="green" variant="light" mb={4}>
                Sudah Diperiksa
              </Badge>
              <Group gap={6}>
                <IconStethoscope size={14} color="gray" />
                <Text fz="xs" c="dimmed">
                  {report.user?.name || "Supervisor"}
                </Text>
              </Group>
            </div>
          ) : (
            <Badge color="gray" variant="outline">
              Belum Input
            </Badge>
          )}
        </Table.Td>

        {/* Kolom Aksi */}
        <Table.Td>
          <Group gap="xs" justify="flex-end">
            {hasReport && (
              <Tooltip label="Lihat Detail Nutrisi">
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  onClick={() => handleViewDetail(item)}
                >
                  <IconEye size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Laporan Nutrisi</Title>
          <Text c="dimmed" size="sm">
            Monitoring nilai gizi makanan yang didistribusikan
          </Text>
        </div>
        <Button
          variant="light"
          onClick={fetchData}
          leftSection={<IconRefresh size={16} />}
        >
          Refresh
        </Button>
      </Group>

      {/* --- UI FILTER --- */}
      <Paper p="md" mb="md" withBorder radius="md" bg="gray.0">
        <Group>
          <Select
            placeholder="Filter Sekolah"
            leftSection={<IconFilter size={16} />}
            data={schoolOptions}
            value={filterSekolah}
            onChange={setFilterSekolah}
            clearable
            searchable
            nothingFoundMessage="Sekolah tidak ditemukan"
            style={{ width: 300 }}
          />
          {filterSekolah && (
            <Text size="sm" c="dimmed">
              Hasil filter: <b>{filteredData.length}</b> data ditemukan
            </Text>
          )}
        </Group>
      </Paper>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        {data.length === 0 ? (
          <Center p="xl" c="dimmed">
            <Group>
              <IconInfoCircle size={20} />
              <Text>Belum ada data distribusi.</Text>
            </Group>
          </Center>
        ) : (
          <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th>Tanggal</Table.Th>
                  <Table.Th>Sekolah</Table.Th>
                  <Table.Th>Menu Makanan</Table.Th>
                  <Table.Th>Status & Petugas</Table.Th>
                  <Table.Th ta="right">Detail</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length > 0 ? (
                  rows
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Center p="xl" c="dimmed">
                        Tidak ada data untuk sekolah <b>{filterSekolah}</b>.
                      </Center>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      {/* --- MODAL DETAIL --- */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group>
            <IconReportMedical size={20} color="#228be6" />
            <Text fw={700}>Detail Laporan Gizi</Text>
          </Group>
        }
        centered
        size="lg"
      >
        {selectedItem && selectedItem.laporan_nutrisi && (
          <Box>
            {/* Header Info Card */}
            <Paper withBorder p="sm" mb="md" bg="gray.0" radius="md">
              <Grid>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                    Sekolah
                  </Text>
                  <Text fw={500}>{selectedItem.sekolah?.nama_sekolah}</Text>
                </Grid.Col>
                <Grid.Col span={6} style={{ textAlign: "right" }}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                    Menu
                  </Text>
                  <Text fw={500}>{selectedItem.menu?.nama_menu}</Text>
                </Grid.Col>

                <Grid.Col span={12} py={0}>
                  <Divider color="gray.3" borderStyle="dashed" />
                </Grid.Col>

                <Grid.Col span={12} mt="xs">
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                        Diperiksa Oleh
                      </Text>
                      <Group gap="xs">
                        <Avatar size="sm" color="blue" radius="xl">
                          <IconStethoscope size={14} />
                        </Avatar>
                        <Text size="sm" fw={600}>
                          {selectedItem.laporan_nutrisi.user?.name ||
                            "Supervisor"}
                        </Text>
                      </Group>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                        Waktu Input
                      </Text>
                      <Group gap={5} justify="flex-end">
                        <IconCalendar size={14} color="gray" />
                        <Text size="sm">
                          {new Date(
                            selectedItem.laporan_nutrisi.created_at
                          ).toLocaleString("id-ID")}
                        </Text>
                      </Group>
                    </div>
                  </Group>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Visualisasi Data Nutrisi */}
            <Text fw={500} mb="xs" size="sm">
              Rincian Kandungan Gizi:
            </Text>

            <Grid mb="lg">
              <Grid.Col span={4}>
                <Paper withBorder p="md" radius="md" ta="center">
                  <ThemeIcon
                    size="lg"
                    radius="xl"
                    color="yellow.6"
                    variant="light"
                    mb="xs"
                  >
                    <IconChartPie size={20} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Lemak
                  </Text>
                  <Text size="xl" fw={700} c="dark">
                    {selectedItem.laporan_nutrisi.lemak}{" "}
                    <span style={{ fontSize: 12 }}>gram</span>
                  </Text>
                </Paper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Paper withBorder p="md" radius="md" ta="center">
                  <ThemeIcon
                    size="lg"
                    radius="xl"
                    color="red.6"
                    variant="light"
                    mb="xs"
                  >
                    <IconChartPie size={20} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Protein
                  </Text>
                  <Text size="xl" fw={700} c="dark">
                    {selectedItem.laporan_nutrisi.protein}{" "}
                    <span style={{ fontSize: 12 }}>gram</span>
                  </Text>
                </Paper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Paper withBorder p="md" radius="md" ta="center">
                  <ThemeIcon
                    size="lg"
                    radius="xl"
                    color="blue.6"
                    variant="light"
                    mb="xs"
                  >
                    <IconChartPie size={20} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Karbohidrat
                  </Text>
                  <Text size="xl" fw={700} c="dark">
                    {selectedItem.laporan_nutrisi.karbohidrat}{" "}
                    <span style={{ fontSize: 12 }}>gram</span>
                  </Text>
                </Paper>
              </Grid.Col>
            </Grid>

            {/* Catatan */}
            <Paper p="sm" bg="blue.0" radius="md">
              <Text size="xs" c="blue.8" fw={700} tt="uppercase" mb={4}>
                Catatan Pemeriksaan
              </Text>
              <Text
                size="sm"
                c="blue.9"
                style={{ fontStyle: "italic", lineHeight: 1.5 }}
              >
                "{selectedItem.laporan_nutrisi.catatan}"
              </Text>
            </Paper>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Tutup
              </Button>
            </Group>
          </Box>
        )}
      </Modal>
    </div>
  );
}